import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { showBrowserNotification } from '../../lib/notifications'
import { useToast } from '../../contexts/ToastContext'
import Modal from '../../components/Modal'
import DatePickerField from '../../components/DatePickerField'
import CustomSelect from '../../components/CustomSelect/CustomSelect'
import './TasksPage.css'

const PRIORITY_LABELS = { urgent: 'Срочно', high: 'Высокий', normal: 'Обычный', low: 'Низкий' }
const STATUS_LABELS = { new: 'Новая', in_progress: 'В работе', done: 'Готово', cancelled: 'Отменена' }
const REPEAT_LABELS = { daily: 'Ежедневно', weekly: 'Еженедельно', monthly: 'Ежемесячно' }

function formatDate(d) {
  if (!d) return '—'
  const date = new Date(d + 'T00:00:00')
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

function isOverdue(task) {
  if (!task.due_date || task.status === 'done' || task.status === 'cancelled') return false
  return task.due_date < new Date().toISOString().split('T')[0]
}

const compressImage = (file, maxWidth = 1024, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height
        
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width)
          width = maxWidth
        }
        
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            })
            resolve(compressedFile)
          } else {
            resolve(file)
          }
        }, file.type, quality)
      }
      img.onerror = (err) => reject(err)
    }
    reader.onerror = (err) => reject(err)
  })
}

const playSuccessSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    ctx.resume().then(() => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = 'sine'
      osc.frequency.setValueAtTime(523.25, ctx.currentTime) // C5
      osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.08) // E5
      osc.frequency.setValueAtTime(783.99, ctx.currentTime + 0.16) // G5
      gain.gain.setValueAtTime(0.15, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.35)
    }).catch(() => {})
  } catch (e) {}
}

const TASK_TEMPLATES = [
  {
    title: 'Ежедневная уборка ПВЗ',
    description: 'Влажная уборка клиентской зоны, протирка столов выдачи, проверка чистоты в примерочных. Выбросить мусор.',
    priority: 'normal',
    repeat_type: 'daily',
    require_photo: true
  },
  {
    title: 'Проверка склада на зависшие ШК',
    description: 'Просканировать склад на предмет зависших товаров, проверить соответствие ячеек в системе.',
    priority: 'high',
    repeat_type: 'weekly',
    require_photo: false
  },
  {
    title: 'Проверка оборудования',
    description: 'Проверить работу сканеров штрихкодов, принтера этикеток, компьютера и камер видеонаблюдения.',
    priority: 'normal',
    repeat_type: 'weekly',
    require_photo: false
  },
  {
    title: 'Генеральная уборка склада',
    description: 'Очистка стеллажей от пыли, мытье полов на складе, разбор невостребованной упаковки.',
    priority: 'high',
    repeat_type: 'monthly',
    require_photo: true
  }
]

const emptyForm = { title: '', description: '', assigned_to: '', priority: 'normal', due_date: '', due_time: '', repeat_type: '', repeat_end_date: '', require_photo: false, is_verified: false, pvz_id: '' }

export default function TasksPage() {
  const { profile, activePvz, userPvzs, selectedPvzIds } = useAuth()
  const toast = useToast()
  const isAdmin = profile && ['owner', 'admin', 'admin_employee'].includes(profile.role)
  const orgId = profile?.org_id

  // === РОЛЕВАЯ МОДЕЛЬ ЗАДАЧ ===
  // Может ли пользователь СОЗДАВАТЬ задачи (все могут - владелец всем, админ сотрудникам, сотрудник друг другу)
  const canCreateTasks = true

  // Являюсь ли я ИСПОЛНИТЕЛЕМ этой задачи
  const isMyTask = (task) => task.assigned_to === profile?.id

  // Являюсь ли я РУКОВОДИТЕЛЕМ этой задачи (могу редактировать/удалять/принимать)
  const isTaskManager = (task) => {
    if (isMyTask(task)) return false // Если задача назначена мне — я ВСЕГДА исполнитель
    if (task.created_by === profile?.id) return true // Я создал эту задачу
    if (profile?.role === 'owner') return true // Владелец может управлять всем
    if (isAdmin) return true // Admin/admin_employee управляют задачами в орг
    return false
  }

  // Может ли текущий пользователь менять статус задачи
  const canChangeStatus = (task) => {
    return isMyTask(task) || isTaskManager(task)
  }

  const [tasks, setTasks] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, done: 0, overdue: 0 })

  // Filters
  const [activeTab, setActiveTab] = useState('active') // active, review, done, all
  const [viewMode, setViewMode] = useState('list') // list, calendar
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [filterAssignee, setFilterAssignee] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  // Modals
  const [showForm, setShowForm] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [formData, setFormData] = useState({ ...emptyForm })
  const [saving, setSaving] = useState(false)

  const [selectedTask, setSelectedTask] = useState(null)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [loadingComments, setLoadingComments] = useState(false)

  const [photoUploadTask, setPhotoUploadTask] = useState(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  // Лайтбокс для фото
  const [lightboxUrl, setLightboxUrl] = useState(null)

  // Отчёт исполнителя при отправке на проверку
  const [completionReport, setCompletionReport] = useState('')

  // Ref для автоскролла комментариев
  const commentsEndRef = useRef(null)

  // Звук входящего уведомления
  const playIncomingSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      ctx.resume().then(() => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.type = 'sine'
        osc.frequency.setValueAtTime(880, ctx.currentTime)
        osc.frequency.setValueAtTime(1046.5, ctx.currentTime + 0.1)
        gain.gain.setValueAtTime(0.12, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.3)
      }).catch(() => {})
    } catch (e) {}
  }, [])

  // Load data
  const loadData = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const [tasksData, emps] = await Promise.all([
        api.getTasks(orgId),
        api.getEmployeesList(null, orgId)
      ])
      setTasks(tasksData)
      setEmployees(emps.filter(e => e.is_approved !== false && !e.is_legacy))
      computeStats(tasksData)
      
      // 16e: Проверяем дедлайны (1 раз за сессию)
      checkDeadlineNotifications(tasksData)
    } catch (err) {
      console.error('Tasks load error:', err)
    } finally {
      setLoading(false)
    }
  }, [orgId, selectedPvzIds])

  useEffect(() => {
    if (!orgId) return
    loadData()
  }, [orgId, loadData, selectedPvzIds])

  // ============ REALTIME ПОДПИСКА ============
  useEffect(() => {
    if (!orgId) return

    const channel = supabase.channel(`tasks_realtime_${orgId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tasks',
        filter: `org_id=eq.${orgId}`
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          // Новая задача
          const newTask = payload.new
          setTasks(prev => {
            if (prev.some(t => t.id === newTask.id)) return prev
            return [newTask, ...prev]
          })
          // Push ответственному (если это не я создал)
          if (newTask.assigned_to === profile?.id && newTask.created_by !== profile?.id) {
            playIncomingSound()
            toast.info(`Новая задача: ${newTask.title}`)
            showBrowserNotification('Новая задача', {
              body: newTask.title,
              tag: 'task-new-' + newTask.id,
              navType: 'tasks',
              targetId: newTask.id,
            })
          }
          // Перезагружаем для получения join-данных (assigned_profile)
          if (window._taskReloadTimeout) clearTimeout(window._taskReloadTimeout)
          window._taskReloadTimeout = setTimeout(() => loadData(), 800)
        } else if (payload.eventType === 'UPDATE') {
          const updated = payload.new
          const isOwnUpdate = window._ownTaskUpdate === updated.id
          setTasks(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t))
          // Обновляем selectedTask только если это НЕ наше обновление (наше уже применено оптимистично)
          if (selectedTask?.id === updated.id && !isOwnUpdate) {
            setSelectedTask(prev => prev ? { ...prev, ...updated } : prev)
          }

          // Уведомления о статусах
          // Задачу взяли в работу — уведомить создателя
          if (updated.status === 'in_progress' && payload.old?.status === 'new' && updated.assigned_to !== profile?.id && updated.created_by === profile?.id) {
            playIncomingSound()
            toast.info(`Задача взята в работу: ${updated.title}`)
            showBrowserNotification('Задача взята в работу', {
              body: updated.title,
              tag: 'task-started-' + updated.id,
              navType: 'tasks',
              targetId: updated.id,
            })
          }

          if (updated.status === 'done' && updated.assigned_to !== profile?.id && updated.created_by === profile?.id) {
            // Задача выполнена — уведомить создателя
            playIncomingSound()
            toast.info(`Задача выполнена: ${updated.title}`)
            showBrowserNotification('Задача выполнена', {
              body: updated.title,
              tag: 'task-done-' + updated.id,
              navType: 'tasks',
              targetId: updated.id,
            })
          }
          if (updated.is_verified === true && updated.assigned_to === profile?.id) {
            playIncomingSound()
            toast.success(`Отчёт одобрен: ${updated.title}`)
            showBrowserNotification('Отчёт одобрен', {
              body: updated.title,
              tag: 'task-approved-' + updated.id,
              navType: 'tasks',
              targetId: updated.id,
            })
          }
          if (updated.status === 'in_progress' && payload.old?.status === 'done' && updated.assigned_to === profile?.id) {
            playIncomingSound()
            toast.warning(`Задача возвращена: ${updated.title}`)
            showBrowserNotification('Отчёт отклонён', {
              body: `Переделайте: ${updated.title}`,
              tag: 'task-rejected-' + updated.id,
              navType: 'tasks',
              targetId: updated.id,
            })
          }

          // Пересчитываем статистику без полной перезагрузки
          setTasks(prev => {
            computeStats(prev)
            return prev
          })
        } else if (payload.eventType === 'DELETE') {
          setTasks(prev => prev.filter(t => t.id !== payload.old?.id))
        }
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'task_comments',
      }, (payload) => {
        const newComment = payload.new
        // Если открыта детализация этой задачи — добавляем комментарий
        if (selectedTask?.id === newComment.task_id && newComment.author_id !== profile?.id) {
          setComments(prev => {
            if (prev.some(c => c.id === newComment.id)) return prev
            return [...prev, { ...newComment, author: { full_name: 'Пользователь' } }]
          })
          // Перезагружаем комментарии для получения join-данных
          api.getTaskComments(newComment.task_id).then(setComments).catch(console.error)
          playIncomingSound()
        }
        // Push если комментарий не от меня
        if (newComment.author_id !== profile?.id) {
          const task = tasks.find(t => t.id === newComment.task_id)
          if (task && (task.assigned_to === profile?.id || task.created_by === profile?.id)) {
            toast.info(`Комментарий по задаче: ${task?.title || 'Задача'}`)
            showBrowserNotification('Новый комментарий', {
              body: task?.title || 'Задача',
              tag: 'task-comment-' + newComment.id,
              navType: 'tasks',
              targetId: newComment.task_id,
            })
          }
        }
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [orgId, profile?.id, selectedTask?.id, playIncomingSound])

  // ============ НАВИГАЦИЯ ИЗ PUSH ============
  useEffect(() => {
    const taskId = localStorage.getItem('wb_task_navigate')
    if (taskId && tasks.length > 0) {
      localStorage.removeItem('wb_task_navigate')
      const task = tasks.find(t => t.id === taskId)
      if (task) {
        setTimeout(() => openTaskDetail(task), 300)
      }
    }
  }, [tasks])

  // ============ АВТОСКРОЛЛ КОММЕНТАРИЕВ ============
  useEffect(() => {
    if (commentsEndRef.current && comments.length > 0) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [comments.length])
  
  const checkDeadlineNotifications = (list) => {
    const sessionKey = 'task_deadline_notified_' + new Date().toISOString().split('T')[0]
    if (sessionStorage.getItem(sessionKey)) return
    
    const today = new Date().toISOString().split('T')[0]
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0]
    
    // Фильтруем задачи пользователя (назначенные ему)
    const myTasks = list.filter(t => 
      t.assigned_to === profile?.id && t.status !== 'done' && t.status !== 'cancelled'
    )
    
    const overdue = myTasks.filter(t => t.due_date && t.due_date < today)
    const dueToday = myTasks.filter(t => t.due_date === today)
    const dueTomorrow = myTasks.filter(t => t.due_date === tomorrow)
    
    if (overdue.length > 0) {
      showBrowserNotification('Просроченные задачи', {
        body: `У вас ${overdue.length} ${overdue.length === 1 ? 'просроченная задача' : 'просроченных задач'}`,
        tag: 'tasks-overdue',
      })
    } else if (dueToday.length > 0) {
      showBrowserNotification('Задачи на сегодня', {
        body: dueToday.map(t => t.title).join(', '),
        tag: 'tasks-today',
      })
    } else if (dueTomorrow.length > 0) {
      showBrowserNotification('Задачи на завтра', {
        body: dueTomorrow.map(t => t.title).join(', '),
        tag: 'tasks-tomorrow',
      })
    }
    
    sessionStorage.setItem(sessionKey, '1')
  }

  const computeStats = (list) => {
    const today = new Date().toISOString().split('T')[0]
    setStats({
      total: list.length,
      new: list.filter(t => t.status === 'new').length,
      in_progress: list.filter(t => t.status === 'in_progress').length,
      done: list.filter(t => t.status === 'done').length,
      overdue: list.filter(t => t.due_date && t.due_date < today && t.status !== 'done' && t.status !== 'cancelled').length,
    })
  }

  // Счётчики для табов
  const tabCounts = useMemo(() => {
    let list = [...tasks]
    if (!isAdmin) {
      list = list.filter(t => t.assigned_to === profile?.id || t.created_by === profile?.id)
    }
    
    return {
      active: list.filter(t => t.status !== 'cancelled' && (t.status === 'new' || t.status === 'in_progress')).length,
      review: list.filter(t => t.status === 'done' && !t.is_verified).length,
      done: list.filter(t => t.status === 'done' && t.is_verified).length,
      all: list.length
    }
  }, [tasks, isAdmin, profile])

  // Вычисляем процент выполненных вовремя задач для текущего сотрудника
  const punctualityStats = useMemo(() => {
    const employeeTasks = tasks.filter(t => {
      if (isAdmin) {
        if (filterAssignee) return t.assigned_to === filterAssignee
        return true
      }
      return t.assigned_to === profile?.id
    })
    
    const completedTasks = employeeTasks.filter(t => t.status === 'done')
    if (completedTasks.length === 0) return null
    
    const onTimeTasks = completedTasks.filter(t => {
      if (!t.due_date) return true
      if (!t.completed_at) return true
      const completedDay = t.completed_at.split('T')[0]
      return completedDay <= t.due_date
    })
    
    const percent = Math.round((onTimeTasks.length / completedTasks.length) * 100)
    return {
      totalCompleted: completedTasks.length,
      onTimeCount: onTimeTasks.length,
      percent
    }
  }, [tasks, profile, isAdmin, filterAssignee])

  // Filtered tasks
  const filtered = useMemo(() => {
    let list = [...tasks]
    
    // Фильтр по вкладкам
    if (activeTab === 'active') {
      list = list.filter(t => t.status !== 'cancelled' && (t.status === 'new' || t.status === 'in_progress'))
    } else if (activeTab === 'review') {
      list = list.filter(t => t.status === 'done' && !t.is_verified)
    } else if (activeTab === 'done') {
      list = list.filter(t => t.status === 'done' && t.is_verified)
    }

    if (filterAssignee) list = list.filter(t => t.assigned_to === filterAssignee)
    if (!isAdmin) list = list.filter(t => t.assigned_to === profile?.id || t.created_by === profile?.id)
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(t => t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q))
    }
    
    // Sort: done at bottom, then overdue first, then by priority
    const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 }
    list.sort((a, b) => {
      const aDone = a.status === 'done' ? 1 : 0
      const bDone = b.status === 'done' ? 1 : 0
      if (aDone !== bDone) return aDone - bDone
      const aOver = isOverdue(a) ? 0 : 1
      const bOver = isOverdue(b) ? 0 : 1
      if (aOver !== bOver) return aOver - bOver
      return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2)
    })
    return list
  }, [tasks, activeTab, filterAssignee, searchQuery, isAdmin, profile])

  // CRUD
  const handleSave = async () => {
    if (!formData.title.trim()) return
    setSaving(true)
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        assigned_to: formData.assigned_to || null,
        priority: formData.priority,
        due_date: formData.due_date || null,
        due_time: formData.due_time || null,
        repeat_type: formData.repeat_type || null,
        repeat_end_date: formData.repeat_type ? (formData.repeat_end_date || null) : null,
        created_by: profile.id,
        require_photo: formData.require_photo || false,
        is_verified: !formData.require_photo,
        pvz_id: formData.pvz_id || activePvz?.id || null,
      }
      if (editingTask) {
        // При редактировании: если требование фотоотчета изменилось
        const isVerifiedVal = editingTask.status === 'done' ? !payload.require_photo : false
        const updated = await api.updateTask(editingTask.id, { ...payload, is_verified: isVerifiedVal })
        setTasks(prev => prev.map(t => t.id === editingTask.id ? updated : t))
        toast.success('Задача обновлена')
      } else {
        const created = await api.createTask(orgId, payload)
        setTasks(prev => [created, ...prev])
        toast.success('Задача создана')
      }
      computeStats(tasks)
      closeForm()
      loadData()
    } catch (err) {
      console.error('Save task error:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = async (task, newStatus) => {
    // При отправке на проверку is_verified ВСЕГДА false — задача всегда ждёт принятия руководителем
    const isVerifiedVal = false
    const optimistic = { 
      ...task, 
      status: newStatus, 
      is_verified: isVerifiedVal,
      completed_at: newStatus === 'done' ? new Date().toISOString() : null,
      completion_photo_url: newStatus !== 'done' ? null : task.completion_photo_url
    }
    // Флаг чтобы Realtime не дублировал уведомления для наших действий
    window._ownTaskUpdate = task.id
    setTimeout(() => { window._ownTaskUpdate = null }, 3000)
    setTasks(prev => {
      const updated = prev.map(t => t.id === task.id ? optimistic : t)
      computeStats(updated)
      return updated
    })
    if (selectedTask?.id === task.id) setSelectedTask(optimistic)
    try {
      await api.updateTask(task.id, { 
        status: newStatus, 
        is_verified: isVerifiedVal,
        completion_photo_url: newStatus !== 'done' ? null : task.completion_photo_url
      })
      
      if (newStatus === 'done') {
        playSuccessSound()
        // Сохраняем отчёт как комментарий
        if (completionReport.trim()) {
          try {
            await api.addTaskComment(task.id, profile.id, `Отчёт о выполнении:\n${completionReport.trim()}`)
            setCompletionReport('')
          } catch(e) { console.warn('Report comment error:', e) }
        }
      }

      // Повторяющаяся задача — при выполнении создаём следующую
      if (newStatus === 'done' && task.repeat_type && task.due_date) {
        try {
          const next = await api.spawnNextRecurringTask(task)
          if (next) {
            setTasks(prev => [next, ...prev])
            const repeatLabel = REPEAT_LABELS[task.repeat_type] || ''
            toast.info(`Следующая: ${formatDate(next.due_date)} (${repeatLabel.toLowerCase()})`)
          }
        } catch (err) {
          console.warn('Recurring task error:', err)
        }
      }
    } catch (err) {
      console.error('Status change error:', err)
      // Откатываем при ошибке
      loadData()
    }
  }

  const handleDelete = async (taskId) => {
    try {
      await api.deleteTask(taskId)
      setTasks(prev => prev.filter(t => t.id !== taskId))
      setSelectedTask(null)
      loadData()
    } catch (err) {
      console.error('Delete error:', err)
    }
  }

  const handleUploadPhotoAndComplete = async () => {
    if (!photoFile || !photoUploadTask) return
    setUploadingPhoto(true)
    try {
      // Сжимаем изображение на клиенте перед загрузкой
      const compressedFile = await compressImage(photoFile, 1024, 0.75)
      const photoUrl = await api.uploadEvidenceFile(compressedFile)
      
      const optimistic = { 
        ...photoUploadTask, 
        status: 'done', 
        completion_photo_url: photoUrl,
        is_verified: false,
        completed_at: new Date().toISOString() 
      }
      setTasks(prev => {
        const updated = prev.map(t => t.id === photoUploadTask.id ? optimistic : t)
        computeStats(updated)
        return updated
      })
      if (selectedTask?.id === photoUploadTask.id) setSelectedTask(optimistic)
      
      await api.updateTask(photoUploadTask.id, { 
        status: 'done', 
        completion_photo_url: photoUrl,
        is_verified: false
      })
      
      try {
        await api.addTaskComment(photoUploadTask.id, profile.id, 'Выполнил задачу и прикрепил фотоотчёт.')
      } catch (e) {
        console.warn('Comment auto creation failed:', e)
      }
      
      playSuccessSound()

      if (photoUploadTask.repeat_type && photoUploadTask.due_date) {
        try {
          const next = await api.spawnNextRecurringTask(photoUploadTask)
          if (next) {
            setTasks(prev => [next, ...prev])
            const repeatLabel = REPEAT_LABELS[photoUploadTask.repeat_type] || ''
            toast.info(`Следующая: ${formatDate(next.due_date)} (${repeatLabel.toLowerCase()})`)
          }
        } catch (err) {
          console.warn('Recurring task error:', err)
        }
      }
      
      toast.success(`Выполнено с фотоотчётом: ${photoUploadTask.title}`)
      setPhotoUploadTask(null)
      setPhotoFile(null)
      setPhotoPreview(null)
      loadData()
    } catch (err) {
      console.error('Photo upload complete error:', err)
      toast.error('Ошибка при загрузке фотоотчета')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleVerifyTask = async (task, approve) => {
    try {
      if (approve) {
        const updated = await api.updateTask(task.id, { is_verified: true })
        setTasks(prev => prev.map(t => t.id === task.id ? updated : t))
        if (selectedTask?.id === task.id) setSelectedTask(updated)
        playSuccessSound()
        toast.success('Фотоотчёт успешно одобрен!')
        // Push сотруднику
        if (task.assigned_to && task.assigned_to !== profile?.id) {
          try {
            await api.sendTaskPush(task.assigned_to, 'task_approved', task.id, task.title, profile.full_name)
          } catch(e) { console.warn('Task push error:', e) }
        }
      } else {
        const updated = await api.updateTask(task.id, { 
          status: 'in_progress', 
          completion_photo_url: null, 
          is_verified: false 
        })
        setTasks(prev => prev.map(t => t.id === task.id ? updated : t))
        if (selectedTask?.id === task.id) setSelectedTask(updated)
        
        try {
          await api.addTaskComment(task.id, profile.id, 'Фотоотчёт отклонён. Требуется переделать задачу.')
          const updatedComments = await api.getTaskComments(task.id)
          setComments(updatedComments)
        } catch (e) {
          console.warn('Comment auto creation failed:', e)
        }
        
        toast.warning('Фотоотчёт отклонён, задача возвращена сотруднику')
        // Push сотруднику
        if (task.assigned_to && task.assigned_to !== profile?.id) {
          try {
            await api.sendTaskPush(task.assigned_to, 'task_rejected', task.id, task.title, profile.full_name)
          } catch(e) { console.warn('Task push error:', e) }
        }
      }
      loadData()
    } catch (err) {
      console.error('Verify task error:', err)
      toast.error('Не удалось обновить статус проверки')
    }
  }

  const handleFastComment = async (text) => {
    if (!selectedTask) return
    try {
      const c = await api.addTaskComment(selectedTask.id, profile.id, text)
      setComments(prev => [...prev, c])
      toast.success('Комментарий отправлен')
    } catch (err) {
      console.error('Fast comment error:', err)
      toast.error('Ошибка отправки комментария')
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      setPhotoFile(file)
      setPhotoPreview(URL.createObjectURL(file))
    } else {
      toast.error('Пожалуйста, выберите файл изображения')
    }
  }

  const handleToggleDone = async (task, e) => {
    e.stopPropagation()
    const newStatus = task.status === 'done' ? 'new' : 'done'
    
    if (newStatus === 'done' && task.require_photo && !task.completion_photo_url) {
      setPhotoUploadTask(task)
      return
    }
    
    await handleStatusChange(task, newStatus)
    if (newStatus === 'done') {
      toast.success(`Выполнено: ${task.title}`)
    } else {
      toast.info(`Возвращена: ${task.title}`)
    }
  }

  // Comments
  const openTaskDetail = async (task) => {
    setSelectedTask(task)
    setLoadingComments(true)
    try {
      const c = await api.getTaskComments(task.id)
      setComments(c)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingComments(false)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return
    try {
      const c = await api.addTaskComment(selectedTask.id, profile.id, newComment.trim())
      setComments(prev => [...prev, c])
      setNewComment('')
    } catch (err) {
      console.error(err)
    }
  }

  // Form helpers
  const openCreateForm = () => {
    setEditingTask(null)
    setFormData({ 
      ...emptyForm, 
      pvz_id: activePvz?.id || '' 
    })
    setShowForm(true)
  }

  const openEditForm = (task) => {
    setEditingTask(task)
    setFormData({
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      priority: task.priority || 'normal',
      due_date: task.due_date || '',
      due_time: task.due_time?.slice(0, 5) || '',
      repeat_type: task.repeat_type || '',
      repeat_end_date: task.repeat_end_date || '',
      require_photo: task.require_photo || false,
      pvz_id: task.pvz_id || '',
    })
    setShowForm(true)
    setSelectedTask(null)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingTask(null)
    setFormData({ ...emptyForm })
  }

  const getAssigneeName = (task) => {
    if (task.assigned_profile?.full_name) return task.assigned_profile.full_name
    const emp = employees.find(e => e.id === task.assigned_to)
    return emp?.full_name || 'Не назначен'
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="tasks-page animate-fade-in">
        <div className="tasks-header">
          <div className="tasks-header-left">
            <div className="tasks-header-icon"></div>
            <div><div className="tasks-title">Задачи</div><div className="tasks-subtitle">Загрузка...</div></div>
          </div>
        </div>
        <div className="tasks-stats">
          {[1,2,3,4].map(i => <div key={i} className="card tasks-stat-card"><div className="skeleton" style={{height:32,width:'60%',margin:'0 auto 8px'}}/><div className="skeleton" style={{height:12,width:'80%',margin:'0 auto'}}/></div>)}
        </div>
        <div className="tasks-list">
          {[1,2,3,4,5].map(i => <div key={i} className="card task-card"><div className="skeleton" style={{width:22,height:22,borderRadius:'50%',flexShrink:0}}/><div style={{flex:1}}><div className="skeleton" style={{height:16,width:`${50+i*8}%`,marginBottom:8}}/><div className="skeleton" style={{height:12,width:`${30+i*5}%`}}/></div></div>)}
        </div>
      </div>
    )
  }

  const todayStr = new Date().toISOString().split('T')[0]
  const todayTasks = tasks.filter(t => t.due_date === todayStr)
  const todayTotal = todayTasks.length
  const todayDone = todayTasks.filter(t => t.status === 'done').length
  const todayPercent = todayTotal > 0 ? Math.round((todayDone / todayTotal) * 100) : 0

  const renderCalendar = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    
    const firstDayOfMonth = new Date(year, month, 1)
    let startDayOfWeek = firstDayOfMonth.getDay() - 1
    if (startDayOfWeek === -1) startDayOfWeek = 6
    
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days = []
    
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null)
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(new Date(year, month, d))
    }
    
    const monthNames = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ]
    
    const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
    
    return (
      <div className="calendar-view animate-fade-in">
        <div className="calendar-header-row">
          <button className="btn btn-secondary btn-sm" onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}>&larr;</button>
          <span className="calendar-current-month">{monthNames[month]} {year}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}>&rarr;</button>
        </div>
        
        <div className="calendar-grid">
          {weekDays.map(wd => (
            <div key={wd} className="calendar-grid-header-cell">{wd}</div>
          ))}
          
          {days.map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} className="calendar-cell empty" />
            
            const dateStr = day.toISOString().split('T')[0]
            const dayTasks = tasks.filter(t => {
              if (t.due_date !== dateStr) return false
              if (!isAdmin && t.assigned_to !== profile.id && t.created_by !== profile.id) return false
              return true
            })
            
            const isToday = new Date().toISOString().split('T')[0] === dateStr
            
            return (
              <div 
                key={dateStr} 
                className={`calendar-cell ${isToday ? 'today' : ''} ${dayTasks.length > 0 ? 'has-tasks' : ''}`}
                onClick={() => {
                  if (isAdmin) {
                    setFormData({ ...emptyForm, due_date: dateStr })
                    setShowForm(true)
                  } else {
                    toast.info(`Задач на этот день: ${dayTasks.length}`)
                  }
                }}
              >
                <span className="calendar-day-number">{day.getDate()}</span>
                <div className="calendar-cell-tasks">
                  {dayTasks.slice(0, 2).map(t => {
                    let statusDot = 'status-dot-new'
                    if (t.status === 'done') {
                      statusDot = (t.require_photo && !t.is_verified) ? 'status-dot-review' : 'status-dot-done'
                    } else if (t.status === 'in_progress') {
                      statusDot = 'status-dot-progress'
                    }
                    
                    return (
                      <div 
                        key={t.id} 
                        className={`calendar-task-item ${statusDot}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          openTaskDetail(t)
                        }}
                        title={t.title}
                      >
                        {t.title}
                      </div>
                    )
                  })}
                  {dayTasks.length > 2 && (
                    <div className="calendar-more-tasks">+{dayTasks.length - 2}</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="tasks-page animate-fade-in">
      {/* Header */}
      <div className="tasks-header">
        <div className="tasks-header-left">
          <div className="tasks-header-icon"></div>
          <div>
            <div className="tasks-title">Задачи</div>
            <div className="tasks-subtitle">Поручения и контроль сроков</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="view-mode-toggle-group">
            <button className={`btn btn-secondary btn-sm ${viewMode === 'list' ? 'active-toggle' : ''}`} onClick={() => setViewMode('list')}>
              Список
            </button>
            <button className={`btn btn-secondary btn-sm ${viewMode === 'calendar' ? 'active-toggle' : ''}`} onClick={() => setViewMode('calendar')}>
              Календарь
            </button>
          </div>
          {canCreateTasks && (
            <button className="btn btn-primary btn-sm" onClick={openCreateForm}>+ Новая задача</button>
          )}
        </div>
      </div>

      {/* Today's Tasks Progress & Punctuality Stats */}
      {(todayTotal > 0 || punctualityStats) && (
        <div style={{ display: 'grid', gridTemplateColumns: (todayTotal > 0 && punctualityStats) ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 20 }}>
          {todayTotal > 0 && (
            <div className="card today-progress-card animate-fade-in" style={{ padding: 18, margin: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                  Прогресс выполнения задач на сегодня
                </span>
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--aurora-mid)' }}>
                  {todayDone} из {todayTotal} ({todayPercent}%)
                </span>
              </div>
              <div className="progress-bar-bg" style={{ height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                <div 
                  className="progress-bar-fill" 
                  style={{ 
                    height: '100%', 
                    width: `${todayPercent}%`, 
                    background: 'linear-gradient(90deg, var(--aurora-start), var(--aurora-mid), var(--aurora-end))', 
                    transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: 10
                  }} 
                />
              </div>
            </div>
          )}

          {punctualityStats && (
            <div className="card punctuality-stats-card animate-fade-in" style={{ padding: 18, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', display: 'block', marginBottom: 4 }}>
                  Рейтинг пунктуальности
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                  Выполнено вовремя: {punctualityStats.onTimeCount} из {punctualityStats.totalCompleted} задач
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{ 
                  fontSize: '22px', 
                  fontWeight: 800, 
                  color: punctualityStats.percent >= 90 ? '#22c55e' : punctualityStats.percent >= 70 ? '#f59e0b' : '#ef4444'
                }}>
                  {punctualityStats.percent}%
                </span>
                <span style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)' }}>Вовремя</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs / Kanban-like switch */}
      {viewMode === 'list' && (
        <div className="tasks-tabs-container">
          {[
            { id: 'active', label: 'Активные', count: tabCounts.active },
            { id: 'review', label: 'На проверке', count: tabCounts.review, badge: tabCounts.review > 0 },
            { id: 'done', label: 'Готово', count: tabCounts.done },
            { id: 'all', label: 'Все', count: tabCounts.all }
          ].map(tab => (
            <button
              key={tab.id}
              className={`tasks-tab ${activeTab === tab.id ? 'tasks-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.label}</span>
              <span className={`tasks-tab-count ${activeTab === tab.id ? 'tasks-tab-count-active' : ''} ${tab.badge ? 'animate-pulse' : ''}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Filters (only for List View) */}
      {viewMode === 'list' && (
        <div className="tasks-filters">
          <input
            className="input tasks-search"
            type="text"
            placeholder="Поиск задач..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {isAdmin && (
            <div style={{ minWidth: '180px' }}>
              <CustomSelect
                value={filterAssignee}
                onChange={setFilterAssignee}
                placeholder="Все сотрудники"
                options={[
                  { value: '', label: 'Все сотрудники' },
                  ...employees.map(emp => ({
                    value: emp.id,
                    label: emp.full_name,
                  })),
                ]}
              />
            </div>
          )}
        </div>
      )}

      {/* Main View Content */}
      {viewMode === 'calendar' ? (
        renderCalendar()
      ) : filtered.length === 0 ? (
        <div className="card tasks-empty">
          <div className="tasks-empty-icon"></div>
          <div className="tasks-empty-title">
            {searchQuery ? 'Ничего не найдено' : activeTab === 'done' ? 'Нет завершённых задач' : activeTab === 'review' ? 'Нет задач на проверке' : 'Задач пока нет'}
          </div>
          <div className="tasks-empty-text">
            {'Создайте задачу или дождитесь назначения'}
          </div>
          {canCreateTasks && !searchQuery && (
            <button className="btn btn-primary" onClick={openCreateForm}>+ Создать задачу</button>
          )}
        </div>
      ) : (
        <div className="tasks-list">
          {filtered.map(task => {
            const isReviewState = task.status === 'done' && task.require_photo && !task.is_verified;
            return (
              <div
                key={task.id}
                className={`card task-card glow-${task.priority} ${task.status === 'done' && !isReviewState ? 'task-done' : ''}`}
                onClick={() => openTaskDetail(task)}
              >
                <div className="task-checkbox" onClick={e => e.stopPropagation()}>
                  <button
                    className={`task-check-btn ${task.status === 'done' ? 'checked' : ''} ${isReviewState ? 'review-state' : ''}`}
                    onClick={e => handleToggleDone(task, e)}
                    disabled={!canChangeStatus(task)}
                  >
                    {isReviewState ? '' : ''}
                  </button>
                </div>
                <div className="task-content">
                  <div className="task-title-row">
                    <span className="task-title">{task.title}</span>
                    {task.priority !== 'normal' && (
                      <span className={`task-priority-badge priority-${task.priority}`}>
                        {PRIORITY_LABELS[task.priority]}
                      </span>
                    )}
                    {task.repeat_type && (
                      <span className="task-repeat-badge">{REPEAT_LABELS[task.repeat_type]}</span>
                    )}
                  </div>
                  <div className="task-meta">
                    {task.assigned_to && (
                      <span className="task-meta-item">{getAssigneeName(task)}</span>
                    )}
                    {task.due_date && (
                      <span className={`task-meta-item ${isOverdue(task) ? 'task-meta-overdue' : ''}`}>
                        {isOverdue(task) ? '' : ''} {formatDate(task.due_date)}
                        {task.due_time ? ` ${task.due_time.slice(0, 5)}` : ''}
                      </span>
                    )}
                    <span className={`task-status-chip ${isReviewState ? 'status-review' : `status-${task.status}`}`}>
                      {isReviewState ? 'На проверке' : STATUS_LABELS[task.status]}
                    </span>
                    {task.require_photo && (
                      <span className="task-meta-item" style={{ color: 'var(--aurora-mid)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        Фотоотчёт
                      </span>
                    )}
                  </div>
                  {task.description && (
                    <div className="task-description-preview">{task.description}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={showForm} title={editingTask ? 'Редактировать задачу' : 'Новая задача'} onClose={closeForm}>
          <div className="task-form">
            {!editingTask && (
              <div className="input-group" style={{ marginBottom: 12 }}>
                <label>Быстрые шаблоны ПВЗ</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {TASK_TEMPLATES.map((tmpl, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ padding: '6px 10px', fontSize: '11px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid var(--surface-border)' }}
                      onClick={() => {
                        setFormData({
                          ...formData,
                          title: tmpl.title,
                          description: tmpl.description,
                          priority: tmpl.priority,
                          repeat_type: tmpl.repeat_type,
                          require_photo: tmpl.require_photo,
                        })
                        toast.info(`Применен шаблон: ${tmpl.title}`)
                      }}
                    >
                      {tmpl.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {userPvzs?.length > 1 && (
              <div className="input-group">
                <CustomSelect
                  label="Пункт выдачи (ПВЗ)"
                  value={formData.pvz_id}
                  onChange={val => setFormData({...formData, pvz_id: val})}
                  options={userPvzs.map(p => ({
                    value: p.id,
                    label: p.name
                  }))}
                />
              </div>
            )}
            <div className="input-group">
              <label>Название *</label>
              <input className="input" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Что нужно сделать?" autoFocus />
            </div>
            <div className="input-group">
              <label>Описание</label>
              <textarea className="input" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Подробности задачи..." rows={3} />
            </div>
            <div className="task-form-row">
              <div className="input-group">
                <CustomSelect
                  label="Ответственный"
                  value={formData.assigned_to}
                  onChange={val => setFormData({...formData, assigned_to: val})}
                  placeholder="Не назначен"
                  options={[
                    { value: '', label: 'Не назначен' },
                    ...employees.map(emp => ({
                      value: emp.id,
                      label: emp.full_name,
                    })),
                  ]}
                />
              </div>
              <div className="input-group">
                <CustomSelect
                  label="Приоритет"
                  value={formData.priority}
                  onChange={val => setFormData({...formData, priority: val})}
                  options={Object.entries(PRIORITY_LABELS).map(([k, v]) => ({
                    value: k,
                    label: v,
                    icon: <svg width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="5" fill={k === 'urgent' ? '#ef4444' : k === 'high' ? '#f97316' : k === 'normal' ? '#22c55e' : '#9ca3af'}/></svg>,
                  }))}
                />
              </div>
            </div>
            <div className="task-form-row">
              <div className="input-group">
                <DatePickerField
                  label="Срок выполнения"
                  value={formData.due_date}
                  onChange={(val) => setFormData({...formData, due_date: val})}
                />
              </div>
              <div className="input-group">
                <label>Время</label>
                <input className="input" type="time" value={formData.due_time} onChange={e => setFormData({...formData, due_time: e.target.value})} />
              </div>
            </div>
            <div className="input-group">
              <CustomSelect
                label="Повторение"
                value={formData.repeat_type}
                onChange={val => setFormData({...formData, repeat_type: val})}
                options={[
                  { value: '', label: 'Без повторения' },
                  ...Object.entries(REPEAT_LABELS).map(([k, v]) => ({
                    value: k,
                    label: v,
                  })),
                ]}
              />
            </div>
            {formData.repeat_type && (
              <div className="input-group">
                <DatePickerField
                  label="Повторять до (необязательно)"
                  value={formData.repeat_end_date}
                  onChange={(val) => setFormData({...formData, repeat_end_date: val})}
                />
              </div>
            )}
            <div className="input-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 }}>
              <input 
                type="checkbox" 
                id="require_photo" 
                checked={formData.require_photo} 
                onChange={e => setFormData({...formData, require_photo: e.target.checked})} 
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <label htmlFor="require_photo" style={{ margin: 0, cursor: 'pointer', textTransform: 'none', fontWeight: 500 }}>
                Требовать фотоотчёт при выполнении задачи
              </label>
            </div>
            <div className="task-form-actions">
              <button className="btn btn-secondary" onClick={closeForm}>Отмена</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !formData.title.trim()}>
                {saving ? 'Сохранение...' : editingTask ? 'Сохранить' : 'Создать задачу'}
              </button>
            </div>
          </div>
        </Modal>

      {/* Task Detail Modal */}
      <Modal isOpen={!!selectedTask} title={selectedTask?.title || ''} onClose={() => setSelectedTask(null)}>
        {selectedTask && (() => {
          const isTaskExecutor = isMyTask(selectedTask)
          const isTaskOwner = isTaskManager(selectedTask)
          return (
          <div className="task-detail">

            {/* === HEADER: Статус + управление === */}
            <div className="task-detail-header">
              {isTaskOwner ? (
                /* РУКОВОДИТЕЛЬ: все кнопки статуса + edit/delete */
                <>
                  <div className="task-detail-status">
                    {['new', 'in_progress', 'done'].map(s => (
                      <button
                        key={s}
                        className={`btn ${selectedTask.status === s ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                        onClick={() => handleStatusChange(selectedTask, s)}
                      >
                        {STATUS_LABELS[s]}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEditForm(selectedTask)}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => { if (confirm('Удалить задачу?')) handleDelete(selectedTask.id) }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
                  </div>
                </>
              ) : isTaskExecutor ? (
                /* ИСПОЛНИТЕЛЬ: показываем текущий статус как badge */
                <div className="task-detail-status">
                  <span className={`task-status-chip status-${selectedTask.status}`}>
                    {STATUS_LABELS[selectedTask.status]}
                  </span>
                  {selectedTask.require_photo && (
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      Требуется фото
                    </span>
                  )}
                </div>
              ) : (
                /* НАБЛЮДАТЕЛЬ */
                <div className="task-detail-status">
                  <span className={`task-status-chip status-${selectedTask.status}`}>
                    {STATUS_LABELS[selectedTask.status]}
                  </span>
                </div>
              )}
            </div>

            {/* === ОПИСАНИЕ === */}
            {selectedTask.description && (
              <div className="task-detail-section">
                <h4>Описание</h4>
                <div className="task-detail-description">{selectedTask.description}</div>
              </div>
            )}

            {/* === ИНФОРМАЦИЯ === */}
            <div className="task-detail-info-grid">
              <div className="task-detail-info-item">
                <span className="label">Ответственный</span>
                <span className="value">{getAssigneeName(selectedTask)}</span>
              </div>
              <div className="task-detail-info-item">
                <span className="label">Приоритет</span>
                <span className="value">
                  <span className={`task-priority-badge priority-${selectedTask.priority}`}>{PRIORITY_LABELS[selectedTask.priority]}</span>
                </span>
              </div>
              <div className="task-detail-info-item">
                <span className="label">Срок</span>
                <span className={`value ${isOverdue(selectedTask) ? 'task-meta-overdue' : ''}`}>
                  {selectedTask.due_date ? `${formatDate(selectedTask.due_date)}${selectedTask.due_time ? ' ' + selectedTask.due_time.slice(0,5) : ''}` : 'Без срока'}
                </span>
              </div>
              {selectedTask.repeat_type && (
                <div className="task-detail-info-item">
                  <span className="label">Повторение</span>
                  <span className="value">{REPEAT_LABELS[selectedTask.repeat_type]}</span>
                </div>
              )}
            </div>

            {/* === ФОТО ПОДТВЕРЖДЕНИЕ (если есть) === */}
            {selectedTask.completion_photo_url && (
              <div className="task-detail-section" style={{ marginTop: 12 }}>
                <h4>Фотоподтверждение</h4>
                <div style={{ marginTop: 8, cursor: 'zoom-in' }} onClick={() => setLightboxUrl(selectedTask.completion_photo_url)}>
                  <img 
                    src={selectedTask.completion_photo_url} 
                    alt="Фотоотчёт" 
                    className="task-photo-thumbnail"
                  />
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: 4, textAlign: 'center' }}>
                    Нажмите для полноэкранного просмотра
                  </div>
                </div>
              </div>
            )}

            {/* ========================================== */}
            {/* === БЛОК ДЕЙСТВИЙ ИСПОЛНИТЕЛЯ === */}
            {/* ========================================== */}
            {isTaskExecutor && selectedTask.status === 'new' && (
              <div className="task-detail-section" style={{ marginTop: 16 }}>
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%', padding: '14px', fontSize: '15px', fontWeight: 700, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  onClick={() => handleStatusChange(selectedTask, 'in_progress')}
                >
                  Взять в работу
                </button>
              </div>
            )}

            {isTaskExecutor && selectedTask.status === 'in_progress' && (
              <div className="task-detail-section" style={{ marginTop: 16, padding: 16, background: 'rgba(99, 102, 241, 0.05)', borderRadius: 'var(--border-radius-md)', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                <h4 style={{ margin: '0 0 12px 0', textTransform: 'none', letterSpacing: 'normal', fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Отправить на проверку
                </h4>

                {/* Поле отчёта */}
                <textarea
                  className="input"
                  placeholder="Опишите что сделано, результат работы..."
                  value={completionReport}
                  onChange={e => setCompletionReport(e.target.value)}
                  rows={3}
                  style={{ width: '100%', marginBottom: 10, resize: 'vertical', minHeight: 80, fontSize: '13px', borderRadius: 10, padding: '10px 12px' }}
                />

                {/* Фото */}
                {selectedTask.require_photo && !selectedTask.completion_photo_url && (
                  <button 
                    className="btn btn-secondary" 
                    style={{ width: '100%', marginBottom: 10, padding: '12px', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 600 }}
                    onClick={() => { setPhotoUploadTask(selectedTask); setSelectedTask(null); }}
                  >
                    Прикрепить фотоотчёт
                  </button>
                )}
                {selectedTask.require_photo && selectedTask.completion_photo_url && (
                  <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: 8, fontSize: '13px', color: '#22c55e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Фото прикреплено
                    <span style={{ marginLeft: 'auto', cursor: 'zoom-in', fontSize: '11px', color: 'var(--text-secondary)' }} onClick={() => setLightboxUrl(selectedTask.completion_photo_url)}>Просмотр</span>
                  </div>
                )}

                {/* Кнопка отправки */}
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%', padding: '14px', fontSize: '15px', fontWeight: 700, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none' }}
                  onClick={() => handleStatusChange(selectedTask, 'done')}
                  disabled={!completionReport.trim() || (selectedTask.require_photo && !selectedTask.completion_photo_url)}
                >
                  Отправить на проверку
                </button>
                {!completionReport.trim() && (
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: 6, textAlign: 'center' }}>
                    Заполните отчёт о выполнении
                  </div>
                )}
              </div>
            )}

            {isTaskExecutor && selectedTask.status === 'done' && (
              <div className="task-detail-section" style={{ marginTop: 12, padding: 14, background: 'rgba(34, 197, 94, 0.06)', borderRadius: 'var(--border-radius-md)', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: 6 }}></div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>Задача отправлена на проверку</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: 4 }}>
                  {selectedTask.is_verified ? 'Руководитель принял выполнение' : 'Ожидайте решения руководителя'}
                </div>
              </div>
            )}

            {/* ========================================== */}
            {/* === БЛОК ДЕЙСТВИЙ РУКОВОДИТЕЛЯ === */}
            {/* ========================================== */}
            {selectedTask.status === 'done' && !selectedTask.is_verified && isTaskOwner && (
              <div className="task-detail-section verification-panel animate-fade-in" style={{ borderLeft: '4px solid #f59e0b', background: 'rgba(245, 158, 11, 0.05)', padding: 16, marginTop: 12, borderRadius: 'var(--border-radius-md)' }}>
                <h4 style={{ color: '#f59e0b', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'none', letterSpacing: 'normal', fontSize: '13px', fontWeight: 600 }}>
                  <span>Требуется ваше решение</span>
                </h4>
                <p style={{ fontSize: '12px', margin: '0 0 12px 0', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  {selectedTask.completion_photo_url 
                    ? 'Сотрудник прикрепил фотоотчёт и отправил на проверку. Примите решение.'
                    : 'Сотрудник отметил задачу как выполненную. Примите решение.'
                  }
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-sm" style={{ flex: 1, gap: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#22c55e', border: 'none', color: '#000', fontWeight: 600, padding: '12px' }} onClick={() => handleVerifyTask(selectedTask, true)}>
                    <span>Принять</span>
                  </button>
                  <button className="btn btn-sm" style={{ flex: 1, gap: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ef4444', border: 'none', color: '#fff', fontWeight: 600, padding: '12px' }} onClick={() => handleVerifyTask(selectedTask, false)}>
                    <span>На доработку</span>
                  </button>
                </div>
              </div>
            )}

            {/* === КОММЕНТАРИИ === */}
            <div className="task-comments">
              <h4 style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Комментарии {comments.length > 0 && `(${comments.length})`}
              </h4>
              {loadingComments ? (
                <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-tertiary)' }}>Загрузка...</div>
              ) : (
                <>
                  {comments.length > 0 && (
                    <div className="task-comments-list">
                      {comments.map(c => (
                        <div key={c.id} className="task-comment">
                          <div className="avatar avatar-sm task-comment-avatar">
                            {c.author?.full_name?.[0] || '?'}
                          </div>
                          <div className="task-comment-body">
                            <div className="task-comment-header">
                              <span className="task-comment-author">{c.author?.full_name || 'Пользователь'}</span>
                              <span className="task-comment-time">{new Date(c.created_at).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div className="task-comment-text">{c.content}</div>
                          </div>
                        </div>
                      ))}
                      <div ref={commentsEndRef} />
                    </div>
                  )}

                  {/* Fast comment templates */}
                  <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--surface-border)' }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6, fontWeight: 600 }}>
                      Быстрые шаблоны ответов
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(isTaskOwner ? [
                          { icon: '', text: 'Одобрено' },
                          { icon: '', text: 'Нужно переделать' },
                          { icon: '', text: 'Жду фото' },
                          { icon: '', text: 'Отличная работа!' }
                        ] : [
                          { icon: '', text: 'Всё готово!' },
                          { icon: '', text: 'Закончу завтра' },
                          { icon: '', text: 'Материалы закончились' },
                          { icon: '', text: 'Нужна помощь' }
                        ]).map((item, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '5px 10px', fontSize: '11px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--surface-border)' }}
                            onClick={() => handleFastComment(`${item.icon} ${item.text}`)}
                          >
                            {item.icon} {item.text}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div className="task-comment-input">
                    <input
                      className="input"
                      placeholder="Написать комментарий..."
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                    />
                    <button className="btn btn-primary btn-sm" onClick={handleAddComment} disabled={!newComment.trim()}>
                      Отправить
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          )
          })()}
      </Modal>

      {/* Photo Upload Modal (for completing tasks with photo requirement) */}
      <Modal 
        isOpen={!!photoUploadTask} 
        title="Подтверждение выполнения" 
        onClose={() => { setPhotoUploadTask(null); setPhotoFile(null); setPhotoPreview(null); }}
      >
        {photoUploadTask && (
          <div className="task-form" style={{ padding: '10px 0' }}>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>Задача:</span>
              <div style={{ fontWeight: 600, fontSize: 'var(--font-size-base)', marginTop: 2 }}>{photoUploadTask.title}</div>
            </div>
            
            <div className="input-group" style={{ marginBottom: 16 }}>
              <label>Прикрепите фотоотчёт *</label>
              {!photoPreview ? (
                <div 
                  style={{
                    border: isDragging ? '2px dashed var(--aurora-mid)' : '2px dashed var(--surface-border)',
                    borderRadius: 'var(--border-radius-md)',
                    padding: '30px 20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: isDragging ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                    transition: 'all var(--transition-fast)'
                  }}
                  onClick={() => document.getElementById('task-photo-input').click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div style={{ fontSize: 32, marginBottom: 8 }}></div>
                  <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Сделать снимок или выбрать фото</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-tertiary)', marginTop: 4 }}>PNG, JPG до 10MB</div>
                  <input 
                    id="task-photo-input"
                    type="file" 
                    multiple
                    accept="image/*,video/*" 
                    style={{ display: 'none' }} 
                    onChange={e => {
                      const file = e.target.files[0]
                      if (file) {
                        setPhotoFile(file)
                        setPhotoPreview(URL.createObjectURL(file))
                      }
                    }} 
                  />
                </div>
              ) : (
                <div style={{ position: 'relative', borderRadius: 'var(--border-radius-md)', overflow: 'hidden' }}>
                  <img src={photoPreview} alt="Превью фотоотчёта" style={{ width: '100%', maxHeight: 250, objectFit: 'cover', borderRadius: 'var(--border-radius-md)' }} />
                  <button 
                    type="button"
                    className="btn btn-danger btn-sm"
                    style={{ position: 'absolute', top: 10, right: 10, borderRadius: '50%', width: 28, height: 28, padding: 0 }}
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                  >
                    
                  </button>
                </div>
              )}
            </div>

            <div className="task-form-actions">
              <button className="btn btn-secondary" onClick={() => { setPhotoUploadTask(null); setPhotoFile(null); setPhotoPreview(null); }}>Отмена</button>
              <button 
                className="btn btn-primary" 
                onClick={handleUploadPhotoAndComplete} 
                disabled={uploadingPhoto || !photoFile}
              >
                {uploadingPhoto ? 'Загрузка фото...' : 'Подтвердить выполнение'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ============ ФОТО ЛАЙТБОКС ============ */}
      {lightboxUrl && (
        <div 
          className="task-lightbox-overlay" 
          onClick={() => setLightboxUrl(null)}
          onKeyDown={(e) => e.key === 'Escape' && setLightboxUrl(null)}
        >
          <button 
            className="task-lightbox-close" 
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
          >
            
          </button>
          <img 
            src={lightboxUrl} 
            alt="Полноэкранный просмотр" 
            className="task-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}
